import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";

const MENU_IMG =
  "https://images.unsplash.com/photo-1767050190883-29d644fa5b99?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY6Nzd8MHwxfHNlYXJjaHw0fHxmaW5lJTIwZGluaW5nJTIwbWVudSUyMHRleHR1cmUlMjBkZXRhaWx8ZW58MHx8fHwxNzkyNzk3NTU5fDA&ixlib=rb-4.1.0&q=85";

const FORMATS = [
  {
    id: "tasting",
    label: "The Tasting",
    subtitle: "7-Course Chef's Tasting Experience",
    note: "Seated, plated and narrated course by course. Composed around the week's market — the menu below is the current season's draft.",
    courses: [
      ["Course I", "Amuse-Bouche", "Compressed Charentais melon, jamón ibérico de bellota, aged balsamic caviar"],
      ["Course II", "Hokkaido Scallop Crudo", "Finger lime, white soy nashi pear, shiso oil, crispy nori"],
      ["Course III", "Black Winter Truffle Agnolotti", "Hand-folded pasta, 36-month Parmigiano Reggiano brodo, cultured butter"],
      ["Course IV", "Wild Line-Caught Turbot", "Sunchoke purée, charred leek emulsion, osetra caviar beurre blanc"],
      ["Course V", "A5 Miyazaki Wagyu Ribcap", "Smoked bone marrow jus, maitake mushrooms, roasted sunchoke crisp"],
      ["Course VI", "Artisanal Cheese Selection", "Brillat-Savarin, Époisses, wild honeycomb, roasted fig sourdough"],
      ["Course VII", "Valrhona Guanaja Chocolate", "70% dark chocolate ganache, gold leaf, smoked sea salt gelato"],
    ],
  },
  {
    id: "banquet",
    label: "The Banquet",
    subtitle: "Family-Style Wedding & Estate Feast",
    note: "Generous shared platters down the centre of long tables — built for weddings and estate dinners where the table itself is the theatre.",
    courses: [
      ["Starter I", "Artisanal Sourdough & Whipped Butter", "House-baked heritage grains, Maldon sea salt, cultured brown butter"],
      ["Starter II", "Charred Octopus & Heirloom Citrus", "Castelvetrano olives, fennel pollen, salsa verde, baby watercress"],
      ["Main I", "Whole Slow-Roasted Heritage Pork", "Herb crust, crackling skin, spiced apple & apricot chutney"],
      ["Main II", "Wild Mushroom & Truffle Risotto", "Chanterelles, porcini dust, aged Carnaroli rice, micro herbs"],
      ["Dessert", "Tahitian Vanilla Crème Brûlée Tart", "Caramelized sugar crust, fresh harvest berries, lavender cream"],
    ],
  },
  {
    id: "reception",
    label: "The Reception",
    subtitle: "Canapés, Raw Bar & Passed Delicacies",
    note: "A standing format for champagne hours and gallery openings — a staged raw bar plus a continuous circuit of passed bites.",
    courses: [
      ["Raw Bar", "Pacific Northwest Oyster Selection", "Champagne mignonette, yuzu pearls, house hot sauce"],
      ["Canapé I", "Wagyu Beef Tartare Crostini", "Cured egg yolk, capers, brioche round, micro mustard greens"],
      ["Canapé II", "Maine Lobster & Cucumber Spoon", "Tarragon aïoli, sevruga caviar, crispy shallot"],
      ["Canapé III", "Wild Forest Mushroom Arancini", "Truffle aïoli, shaved black truffle, crispy sage leaf"],
      ["Sweet Bites", "Miniature Gold Leaf Rocher & Macarons", "Valrhona chocolate, salted caramel, pistachio cream"],
    ],
  },
];

export const MenuSection = () => {
  const [active, setActive] = useState("tasting");
  const format = FORMATS.find((f) => f.id === active);

  return (
    <section id="menus" data-testid="full-menu-section" className="bg-noir py-24 text-bone sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="mb-14">
          <p className="mb-4 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-bone/50">
            <span className="inline-block h-px w-10 bg-gold" />
            The Menus — Three Formats
          </p>
          <h2 className="max-w-2xl font-serif text-3xl leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            Choose the shape of <span className="italic text-gold">the evening</span>
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mb-14 flex flex-wrap gap-3" role="tablist" aria-label="Menu formats">
            {FORMATS.map((f) => (
              <button
                key={f.id}
                role="tab"
                aria-selected={active === f.id}
                data-testid={`menu-tab-${f.id}`}
                onClick={() => setActive(f.id)}
                className={`relative rounded-full border px-6 py-3 font-serif text-lg transition-colors duration-300 ${
                  active === f.id
                    ? "border-gold bg-gold text-noir"
                    : "border-bone/20 text-bone/70 hover:border-gold/60 hover:text-bone"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="grid gap-14 lg:grid-cols-[0.85fr_1.15fr]">
          <Reveal delay={0.15} className="lg:sticky lg:top-28 lg:self-start">
            <div className="arch-frame overflow-hidden border border-bone/15">
              <img
                src={MENU_IMG}
                alt="Fine dining menu detail with elegant table setting"
                loading="lazy"
                data-testid="menu-texture-image"
                className="aspect-[4/5] w-full object-cover"
              />
            </div>
            <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-gold">
              {format.subtitle}
            </p>
            <p className="mt-4 text-sm leading-relaxed text-bone/70 sm:text-base">{format.note}</p>
            <a
              href={`mailto:kas@kaseybeaumont.com?subject=${encodeURIComponent(`Menu Enquiry — ${format.label}`)}`}
              data-testid="menu-request-button"
              className="group mt-7 inline-flex items-center gap-2 rounded-full border border-gold px-6 py-3 text-[12px] font-medium uppercase tracking-[0.16em] text-gold transition-colors duration-300 hover:bg-gold hover:text-noir"
            >
              Request This Menu
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </Reveal>

          <div className="relative min-h-[420px]">
            <AnimatePresence mode="wait">
              <motion.ul
                key={format.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`menu-course-list-${format.id}`}
              >
                {format.courses.map(([num, title, details], i) => (
                  <li
                    key={num}
                    data-testid={`menu-course-${format.id}-${i + 1}`}
                    className="group border-b border-bone/10 py-6 first:pt-0"
                  >
                    <div className="flex items-baseline justify-between gap-6">
                      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold/80">
                        {num}
                      </span>
                      <span className="hidden h-px flex-1 bg-bone/10 sm:block" aria-hidden="true" />
                    </div>
                    <h3 className="mt-2 font-serif text-2xl tracking-tight text-bone transition-colors duration-300 group-hover:text-gold sm:text-3xl">
                      {title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-bone/55">{details}</p>
                  </li>
                ))}
              </motion.ul>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
